/**
 * RemoteApi over supabase-js. Maps PostgREST errors onto the three kinds the
 * engine distinguishes: network (retry later), auth (pause), rejected
 * (surface, never retry).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SyncTable } from '../types';
import { RemoteError, type RemoteApi, type RemoteRow } from './remote';

const REJECT_CODES = new Set(['42501', '23505', '23503', '23514', '22P02', 'PGRST116']);

export function classify(err: unknown): RemoteError {
  const e = err as { code?: string; message?: string; status?: number; name?: string } | undefined;
  const msg = e?.message ?? String(err);
  const code = e?.code ?? (e?.status ? String(e.status) : undefined);
  if (e?.name === 'TypeError' || /fetch failed|network|Failed to fetch|Load failed/i.test(msg)) {
    return new RemoteError(msg, 'network', code);
  }
  if (code === '401' || code === 'PGRST301' || /JWT|not authenticated|invalid claim/i.test(msg)) {
    return new RemoteError(msg, 'auth', code);
  }
  if ((code && REJECT_CODES.has(code)) || /row-level security|violates/i.test(msg)) {
    return new RemoteError(msg, 'rejected', code);
  }
  return new RemoteError(msg, 'other', code);
}

export class SupabaseRemote implements RemoteApi {
  constructor(private readonly sb: SupabaseClient) {}

  async upsert(table: SyncTable, rows: Record<string, unknown>[]): Promise<RemoteRow[]> {
    const { data, error } = await this.sb.from(table).upsert(rows, { onConflict: 'id' }).select();
    if (error) throw classify(error);
    return (data ?? []) as RemoteRow[];
  }

  async softDelete(table: SyncTable, ids: string[]): Promise<void> {
    const { error } = await this.sb
      .from(table)
      .update({ deleted_at: new Date().toISOString() })
      .in('id', ids)
      .is('deleted_at', null);
    if (error) throw classify(error);
  }

  async restore(table: SyncTable, ids: string[]): Promise<void> {
    const { error } = await this.sb.from(table).update({ deleted_at: null }).in('id', ids);
    if (error) throw classify(error);
  }

  async pull(table: SyncTable, companyId: string, since: string | null): Promise<{ rows: RemoteRow[]; serverNow: string }> {
    const { data, error } = await this.sb.rpc('pull_changes', { tbl: table, c: companyId, since });
    if (error) throw classify(error);
    const d = data as { rows: RemoteRow[]; server_now: string };
    return { rows: d.rows ?? [], serverNow: d.server_now };
  }
}
